import {
  Box,
  Center,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FC } from 'react';
import {
  CurrentAuthState,
  useAuthContext,
} from '../../../contexts/AuthContext';
import { Logo } from '../../atoms/Logo';
import { ForgotPasswordSendCode } from './forms/ForgotPasswordSendCode';
import { ForgotPasswordUpdate } from './forms/ForgotPasswordUpdate';
import { MfaCode } from './forms/MfaCode';
import { MfaSelect } from './forms/MfaSelect';
import { MfaTotpSetup } from './forms/MfaTotpSetup';
import { NewPassword } from './forms/NewPassword';
import { SignIn } from './forms/SignIn';

const getTitle = (currentAuthState: CurrentAuthState) => {
  switch (currentAuthState) {
    case CurrentAuthState.ForgotPassword:
      return 'Password Reset';
    case CurrentAuthState.MfaSelection:
    case CurrentAuthState.MfaEmailCode:
    case CurrentAuthState.MfaTotpCode:
    case CurrentAuthState.MfaTotpSetup:
      return 'Two-Factor Authentication';
    default:
      return 'Merkatus.io';
  }
};

export const AuthUIContainer: FC = () => {
  const {
    signIn,
    completeNewPassword,
    forgotPasswordStart,
    cancelForgotPassword,
    forgotPasswordSendCode,
    forgotPasswordUpdatePassword,
    selectMfaType,
    confirmMfaCode,
    confirmTotpSetupDuringSignIn,
    cancelMfa,
    currentAuthState,
    mfaAllowedTypes,
    mfaCodeDestination,
    totpSetupUri,
    totpSetupSecret,
  } = useAuthContext();

  const boxBg = useColorModeValue(
    'background.light.highest',
    'background.dark.highest',
  );
  const boxBorderColor = useColorModeValue(
    'border.light.box',
    'border.dark.box',
  );

  return (
    <Center>
      <Box
        width="100%"
        maxWidth="760px"
        py="16px"
        borderColor={boxBorderColor}
        background={boxBg}>
        <Flex direction="column" width="100%">
          <Flex direction="column" alignItems="center">
            <Logo height="100px" width="100px" />
            <Text
              fontSize="32px"
              fontWeight="bold"
              py="28px"
              color="text.title"
              fontFamily="marketingPage">
              {getTitle(currentAuthState)}
            </Text>
          </Flex>
          {currentAuthState === CurrentAuthState.SignedOut && (
            <SignIn onSubmit={signIn} onForgotPassword={forgotPasswordStart} />
          )}
          {currentAuthState === CurrentAuthState.ForcePasswordChange && (
            <NewPassword onSubmit={completeNewPassword} />
          )}
          {currentAuthState === CurrentAuthState.ForgotPassword && (
            <ForgotPasswordSendCode
              onSubmit={forgotPasswordSendCode}
              onCancel={cancelForgotPassword}
            />
          )}
          {currentAuthState === CurrentAuthState.PasswordReset && (
            <ForgotPasswordUpdate onSubmit={forgotPasswordUpdatePassword} />
          )}
          {currentAuthState === CurrentAuthState.MfaSelection && (
            <MfaSelect
              allowedTypes={mfaAllowedTypes}
              onSelect={selectMfaType}
              onCancel={cancelMfa}
            />
          )}
          {currentAuthState === CurrentAuthState.MfaEmailCode && (
            <MfaCode
              description={`We sent a verification code to ${
                mfaCodeDestination ?? 'your email'
              }. Enter it below to sign in.`}
              hint="Didn't get a code? Go back and sign in again to receive a new one."
              onSubmit={confirmMfaCode}
              onCancel={cancelMfa}
            />
          )}
          {currentAuthState === CurrentAuthState.MfaTotpCode && (
            <MfaCode
              description="Enter the 6-digit code from your authenticator app to sign in."
              onSubmit={confirmMfaCode}
              onCancel={cancelMfa}
            />
          )}
          {currentAuthState === CurrentAuthState.MfaTotpSetup && (
            <MfaTotpSetup
              setupUri={totpSetupUri}
              secret={totpSetupSecret}
              onSubmit={confirmTotpSetupDuringSignIn}
              onCancel={cancelMfa}
            />
          )}
          {currentAuthState === CurrentAuthState.Loading && (
            <Center>
              <Spinner />
            </Center>
          )}
        </Flex>
      </Box>
    </Center>
  );
};
